import React, { useEffect, useState, useRef } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useSelector } from 'react-redux'
import MiniHeader from '../../../../components/layout/MiniHeader'
import Typography from '../../../../components/common/Typography'
import Button from '../../../../components/common/Button'
import LoadingIndicator from '../../../../components/state/LoadingIndicator'
import ErrorState from '../../../../components/state/ErrorState'
import useChat from '../../hooks/useChat'
import { formatDateTime } from '../../../../shared/utils/dateUtils'
import { IoArrowBack, IoSend } from 'react-icons/io5'
import { Avatar } from '@mui/material'
import { FiUsers } from 'react-icons/fi'

const ChatDetail = () => {
  const { userId, conversationId } = useParams()
  const navigate = useNavigate()
  const messagesEndRef = useRef(null)
  const { getConversationMessages, sendMessage, markAsRead } = useChat()

  const { messages, currentConversation, loading, error } = useSelector(state => state.chat)
  const [newMessage, setNewMessage] = useState('')
  const [sending, setSending] = useState(false)

  useEffect(() => {
    if (conversationId) {
      getConversationMessages(conversationId)
      markAsRead(conversationId)
    }
  }, [conversationId, getConversationMessages, markAsRead])

  useEffect(() => {
    scrollToBottom()
  }, [messages])

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }

  const handleSendMessage = async () => {
    if (!newMessage.trim()) return

    try {
      setSending(true)
      await sendMessage(conversationId, newMessage)
      setNewMessage('')
    } catch (error) {
      console.error('Failed to send message:', error)
    } finally {
      setSending(false)
    }
  }

  const handleKeyPress = e => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSendMessage()
    }
  }

  const handleBackToList = () => {
    navigate(`/chats/${userId}`)
  }

  return (
    <>
      <MiniHeader title="Chat Details" />
      <div className="ml-[250px] p-8 w-[1050px] mx-auto my-0">
        {/* Header */}
        <div className="bg-white rounded-lg p-4 mb-4">
          <div className="flex items-center gap-4">
            <Button
              variant="secondary"
              size="small"
              onClick={handleBackToList}
              className="flex items-center gap-2"
            >
              <IoArrowBack />
              Back
            </Button>
            <div className="flex items-center gap-3 flex-1">
              {currentConversation?.isGroup ? (
                <div className="w-10 h-10 bg-teal-500 rounded-full flex items-center justify-center">
                  <FiUsers className="text-white text-lg" />
                </div>
              ) : (
                <Avatar
                  src={currentConversation?.participantAvatar || '/assets/images/default-avatar.jpg'}
                  alt={currentConversation?.participantName}
                  className="w-10 h-10"
                />
              )}
              <div>
                <Typography className="font-semibold text-gray-900">
                  {currentConversation?.isGroup
                    ? currentConversation?.groupName
                    : currentConversation?.participantName || 'Unknown User'}
                </Typography>
                {currentConversation?.isGroup && (
                  <Typography className="text-xs text-gray-500">
                    {currentConversation?.participantCount || 0} participants
                  </Typography>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Messages Container */}
        <div className="bg-white rounded-lg mb-4">
          {loading && <LoadingIndicator />}
          {!loading && error && <ErrorState error={error} />}
          {!loading && !error && (
            <>
              <div className="h-[500px] overflow-y-auto p-6 space-y-4">
                {messages.length === 0 ? (
                  <div className="flex items-center justify-center h-full">
                    <Typography variant="body" className="text-gray-500">
                      No messages yet. Start the conversation!
                    </Typography>
                  </div>
                ) : (
                  messages.map((msg, index) => (
                    <div
                      key={msg._id || index}
                      className={`flex ${msg.isFromUser ? 'justify-end' : 'justify-start'}`}
                    >
                      <div
                        className={`max-w-[70%] rounded-lg p-3 ${
                          msg.isFromUser
                            ? 'bg-teal-500 text-white'
                            : 'bg-gray-100 text-gray-900'
                        }`}
                      >
                        {!msg.isFromUser && currentConversation?.isGroup && (
                          <Typography className="text-xs font-semibold mb-1">
                            {msg.senderName}
                          </Typography>
                        )}
                        <Typography className="text-sm break-words">{msg.message}</Typography>
                        <Typography
                          className={`text-xs mt-1 ${
                            msg.isFromUser ? 'text-teal-100' : 'text-gray-500'
                          }`}
                        >
                          {formatDateTime(msg.timestamp)}
                        </Typography>
                      </div>
                    </div>
                  ))
                )}
                <div ref={messagesEndRef} />
              </div>

              {/* Message Input */}
              <div className="border-t border-gray-200 p-4">
                <div className="flex items-end gap-3">
                  <textarea
                    value={newMessage}
                    onChange={e => setNewMessage(e.target.value)}
                    onKeyPress={handleKeyPress}
                    placeholder="Type your message..."
                    rows={2}
                    disabled={sending}
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-md resize-none focus:outline-none focus:ring-2 focus:ring-teal-500"
                  />
                  <Button
                    onClick={handleSendMessage}
                    disabled={!newMessage.trim() || sending}
                    className="bg-teal-500 hover:bg-teal-600 text-white px-6 py-2 rounded-md flex items-center gap-2"
                  >
                    <IoSend />
                    {sending ? 'Sending...' : 'Send'}
                  </Button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </>
  )
}

export default ChatDetail
